import { getCookieByName, setCookie } from "@stadium/helpers/cookie";
import { logWeglotError } from "@stadium/services/weglot/log-error";

import {
  DEFAULT_LANG_OBJ,
  FORCE_TRANSLATIONS_QUERY_PARAM,
  WEGLOT_LANG,
} from "@stadium/services/weglot/constants";
import { BYSTADIUM_DOMAIN } from "@stadium/constants/domain";

export const isForceTranslationsEnabled = (query) => {
  if (query == null) return false;
  const raw =
    typeof query.get === "function"
      ? query.get(FORCE_TRANSLATIONS_QUERY_PARAM)
      : query[FORCE_TRANSLATIONS_QUERY_PARAM];
  const v = Array.isArray(raw) ? raw[0] : raw;
  return v === "true";
};

export const getSelectedLanguage = ({
  weglotLanguages,
  langInUrl,
  weglotInstance,
  forceTranslations = false,
}) => {
  const getIsLangInWeglot = (lang) => weglotLanguages.some(({ key }) => key === lang);

  if (!forceTranslations) {
    const langStoredInCookie = getCookieByName(WEGLOT_LANG) || weglotInstance?.getCurrentLang();

    if (!langStoredInCookie && getIsLangInWeglot(langInUrl)) {
      return langInUrl;
    }
    const defaultLangToBeSelected = getIsLangInWeglot(langStoredInCookie)
      ? langStoredInCookie
      : DEFAULT_LANG_OBJ.key;
    return defaultLangToBeSelected;
  }

  const langStoredInCookie = getCookieByName(WEGLOT_LANG);

  let selectedLang = null;
  if (!weglotInstance && !weglotLanguages?.length) return selectedLang;

  if (langInUrl && getIsLangInWeglot(langInUrl)) {
    selectedLang = langInUrl;
  } else {
    selectedLang = getIsLangInWeglot(langStoredInCookie)
      ? langStoredInCookie
      : DEFAULT_LANG_OBJ.key;
  }

  if (selectedLang && selectedLang !== langStoredInCookie) {
    setWeglotLangCookie(selectedLang);
  }

  return selectedLang;
};

export const setWeglotLangCookie = (cvalue) => {
  let t = new Date();
  t.setMonth(t.getMonth() + 1);

  setCookie(WEGLOT_LANG, cvalue, { domain: BYSTADIUM_DOMAIN, time: t.toUTCString() });
};

/**
 * Core fetch helper shared by translateWord and translateWords.
 * Returns the raw `to_words` array, or null on a non-OK response.
 * Throws on network errors so callers can catch and fall back.
 */
const fetchTranslations = async ({ apiKey, words, from, to }) => {
  const translatePath = `https://cdn-api-weglot.com/translate?${new URLSearchParams({
    api_key: apiKey,
    v: 1,
  })}`;

  const response = await fetch(translatePath, {
    method: "POST",
    cache: "no-store",
    body: JSON.stringify({
      l_from: from,
      l_to: to,
      request_url: window.location.origin + window.location.pathname,
      words: words.map((w) => ({ t: 1, w })),
    }),
  });

  if (!response.ok) {
    logWeglotError("Weglot translate API request failed", {
      extra: { phase: "translate-api", status: response.status, from, to },
    });
    return null;
  }

  const { to_words } = await response.json();
  return to_words ?? null;
};

export const translateWord = async ({ apiKey, text, from, to = "en" }) => {
  if (!apiKey || !text) return undefined;

  try {
    const results = await fetchTranslations({ apiKey, words: [text], from, to });
    return results?.[0];
  } catch (error) {
    logWeglotError("Weglot translate API request error", {
      error,
      extra: { phase: "translate-api", from, to },
    });
    return undefined;
  }
};

/**
 * Translate multiple texts in a single Weglot API request.
 * Returns a same-length array of translated strings.
 * Falls back to the original text for any entry that fails or is missing.
 */
export const translateWords = async ({ apiKey, texts, from, to = "en" }) => {
  if (!apiKey || !texts?.length) return texts ?? [];

  try {
    const results = await fetchTranslations({ apiKey, words: texts, from, to });
    if (!results) return texts;
    return texts.map((original, i) => results[i] ?? original);
  } catch (error) {
    logWeglotError("Weglot batch translate API request error", {
      error,
      extra: { phase: "translate-api-batch", from, to },
    });
    return texts;
  }
};
